/**
 * Getting Started Articles
 */

import type { HelpArticle } from "../types";

export const GETTING_STARTED_ARTICLES: HelpArticle[] = [
  {
    id: "gs-001",
    slug: "what-is-supabank",
    title: "What is Supabank?",
    description: "Learn about Supabank and how it helps you convert crypto to fiat",
    category: "getting-started",
    popular: true,
    order: 1,
    keywords: ["introduction", "about", "overview", "crypto", "fiat"],
    content: `
# What is Supabank?

Supabank is an institutional crypto liquidation platform that enables you to seamlessly convert cryptocurrency into fiat currency and receive it directly in your bank account.

## How It Works

1. **Verify Your Identity** - Complete KYC verification to unlock all features
2. **Create a Liquidation Address** - Generate a unique crypto address for deposits
3. **Deposit Crypto** - Send supported cryptocurrencies to your address
4. **Automatic Conversion** - Your crypto is automatically converted to fiat
5. **Receive Funds** - Money is transferred to your connected bank account

## Key Features

- **Multi-chain Support** - Accept deposits from Ethereum, Polygon, Solana, and more
- **Multiple Currencies** - Receive payouts in USD, EUR, or MXN
- **Instant Conversion** - Crypto is converted at competitive market rates
- **Bank Integration** - Direct transfers to your verified bank account
- **24/7 Availability** - Deposit and convert anytime

## Who Is Supabank For?

Supabank serves:
- Businesses needing to convert crypto revenue to fiat
- Individuals who receive crypto payments
- Traders looking to off-ramp efficiently
- Companies managing treasury in crypto

## Getting Started

Ready to begin? [Create your account](/auth) and complete verification to start converting crypto to fiat.
    `.trim(),
  },
  {
    id: "gs-002",
    slug: "creating-your-account",
    title: "Creating Your Account",
    description: "Step-by-step guide to setting up your Supabank account",
    category: "getting-started",
    popular: true,
    order: 2,
    keywords: ["signup", "register", "account", "create", "new"],
    content: `
# Creating Your Account

Setting up your Supabank account takes just a few minutes.

## Step 1: Sign Up

1. Visit [supabank.xyz](/auth)
2. Enter your email address
3. Click "Continue with Email"
4. Check your inbox for the magic link
5. Click the link to verify your email

## Step 2: Complete Your Profile

After email verification:
1. Enter your full legal name (as it appears on ID)
2. Provide your date of birth
3. Enter your residential address
4. Select your country of residence

## Step 3: Verify Your Identity (KYC)

To access all features, you'll need to:
1. Upload a government-issued ID (passport, driver's license, or national ID)
2. Take a selfie for identity verification
3. Wait for approval (usually 2-5 minutes)

## What You'll Need

- Valid email address
- Government-issued photo ID
- Proof of address (for some regions)
- Smartphone or webcam for selfie

## Account Types

**Individual Account**
- For personal use
- Standard verification required
- Lower limits initially

**Business Account**
- For companies and organizations
- Additional business documentation required
- Higher limits available

## Tips for Quick Approval

- Use a high-quality, recent photo ID
- Ensure good lighting for your selfie
- Make sure all text on documents is clearly readable
- Use the name exactly as it appears on your ID

## Next Steps

Once verified, you can:
- Add your bank account for payouts
- Create liquidation addresses
- Start receiving crypto deposits
    `.trim(),
  },
  {
    id: "gs-003",
    slug: "kyc-verification-process",
    title: "KYC Verification Process",
    description: "Everything you need to know about identity verification",
    category: "getting-started",
    popular: true,
    order: 3,
    keywords: ["kyc", "verification", "identity", "documents", "id"],
    content: `
# KYC Verification Process

KYC (Know Your Customer) verification is required to use Supabank's services. This helps us comply with regulations and protect your account.

## Why KYC Is Required

- Regulatory compliance (AML/KYC laws)
- Fraud prevention
- Account security
- Higher transaction limits

## Verification Steps

### 1. Personal Information
- Full legal name
- Date of birth
- Residential address
- Nationality

### 2. Document Upload

**Accepted Documents:**
- Passport (recommended)
- Driver's license
- National ID card

**Document Requirements:**
- Must be valid (not expired)
- Clear, readable photo
- All four corners visible
- No glare or reflections

### 3. Selfie Verification
- Take a live photo of your face
- Match your ID photo
- Good lighting required
- No glasses or face coverings

## Verification Timeline

| Status | Typical Time |
|--------|--------------|
| Automatic approval | 2-5 minutes |
| Manual review | Up to 24 hours |
| Additional documents needed | Varies |

## Common Issues

**Document Rejected?**
- Ensure document is not expired
- Retake photo with better lighting
- Make sure all text is readable

**Selfie Failed?**
- Remove glasses and hats
- Face the camera directly
- Use good, natural lighting

## Verification Levels

| Level | Requirements | Limits |
|-------|--------------|--------|
| Basic | Email verification | View only |
| Standard | ID + Selfie | $50,000/month |
| Enhanced | Additional documents | $500,000/month |

## Privacy & Security

- Documents are encrypted in transit
- Stored securely with limited access
- Deleted after verification (per policy)
- Never shared with third parties

## Need Help?

If your verification is taking longer than expected, contact our support team.
    `.trim(),
  },
  {
    id: "gs-004",
    slug: "supported-countries",
    title: "Supported Countries & Restrictions",
    description: "Where Supabank services are available",
    category: "getting-started",
    order: 4,
    keywords: ["countries", "regions", "restrictions", "availability", "supported"],
    content: `
# Supported Countries & Restrictions

Supabank operates in most countries worldwide, with some exceptions due to regulatory requirements.

## Supported Regions

Supabank is available in:
- **North America**: USA (most states), Canada
- **Europe**: EU member states, UK, Switzerland
- **Asia-Pacific**: Japan, Singapore, Hong Kong, Australia
- **Latin America**: Mexico, Brazil, Argentina
- **Other**: Most countries not listed as restricted

## Restricted Countries

**Supabank does NOT serve residents from:**

| Country | Reason |
|---------|--------|
| Russia | International sanctions |
| Belarus | International sanctions |
| Iran | OFAC sanctions |
| North Korea | OFAC sanctions |
| Syria | OFAC sanctions |
| Cuba | OFAC sanctions |
| Venezuela | OFAC sanctions |
| Myanmar | OFAC sanctions |

## US State Restrictions

**Not available in:**
- New York (BitLicense requirements)
- Texas (Additional licensing requirements)

All other US states are supported.

## How We Verify Location

During registration, we verify your location using:
- IP address geolocation
- ID document country of issue
- Residential address verification
- OFAC sanctions list screening

## Important Notes

⚠️ **VPN Usage**: Using a VPN to bypass geographic restrictions is prohibited and will result in account termination.

⚠️ **Document Fraud**: Submitting false documents is illegal and will result in permanent ban.

## Moving Countries?

If you relocate to a different country:
1. Update your profile with new address
2. You may need to re-verify your identity
3. Some features may change based on new location

## Questions?

Contact support if you're unsure about availability in your region.
    `.trim(),
  },
  {
    id: "gs-005",
    slug: "account-security-basics",
    title: "Account Security Basics",
    description: "Essential security practices for your account",
    category: "getting-started",
    order: 5,
    keywords: ["security", "password", "protection", "safe", "secure"],
    content: `
# Account Security Basics

Protecting your Supabank account is essential. Follow these best practices to keep your funds safe.

## Authentication Methods

### Magic Link (Email)
- We use passwordless authentication
- A secure link is sent to your email
- Links expire after 15 minutes
- One-time use only

### Two-Factor Authentication (2FA)
- Adds an extra layer of security
- Required for high-value operations
- Supports authenticator apps

## Security Best Practices

### Email Security
Use a unique, secure email for Supabank
Enable 2FA on your email account
Never share login links
Don't use public computers for login

### Device Security
Keep your devices updated
Use screen lock on mobile
Only use trusted networks
Don't save login details in browsers

### Recognizing Scams
We'll never ask for your password
We'll never request funds via chat
Check URLs carefully (supabank.xyz only)
Don't click suspicious links

## Session Management

- Sessions automatically expire after inactivity
- You can view active sessions in settings
- Sign out from all devices if concerned
- We notify you of new device logins

## What We Do To Protect You

- End-to-end encryption for sensitive data
- Regular security audits
- Fraud monitoring systems
- Cold storage for crypto assets
- Compliance with SOC 2 standards

## Reporting Security Issues

If you notice suspicious activity:
1. Contact support immediately
2. Do not share account details
3. Review recent transactions
4. Consider enabling additional 2FA

## Lost Access?

If you've lost access to your email:
1. Contact support with identity verification
2. Provide alternative proof of identity
3. Account recovery typically takes 24-48 hours
    `.trim(),
  },
  {
    id: "gs-006",
    slug: "two-factor-authentication",
    title: "Two-Factor Authentication (2FA)",
    description: "How to set up and use 2FA",
    category: "getting-started",
    order: 6,
    keywords: ["2fa", "two-factor", "authentication", "security", "authenticator"],
    content: `
# Two-Factor Authentication (2FA)

Two-factor authentication adds an extra layer of security to your account by requiring a second form of verification.

## Why Use 2FA?

- Protects against unauthorized access
- Required for high-value transactions
- Industry-standard security practice
- Peace of mind for your funds

## Supported 2FA Methods

### Authenticator App (Recommended)
- Google Authenticator
- Authy
- Microsoft Authenticator
- 1Password

### Email Verification
- One-time codes sent to email
- Fallback if authenticator unavailable

## Setting Up 2FA

### Step 1: Enable in Settings
1. Go to Settings > Security
2. Click "Enable 2FA"
3. Choose your preferred method

### Step 2: Scan QR Code
1. Open your authenticator app
2. Scan the QR code displayed
3. Enter the 6-digit code to confirm

### Step 3: Save Backup Codes
- Download your backup codes
- Store them securely offline
- Each code can only be used once
- Use if you lose your authenticator

## When 2FA Is Required

| Action | 2FA Required |
|--------|--------------|
| Login from new device | Yes |
| Changing bank account | Yes |
| Large withdrawals | Yes |
| Profile changes | Sometimes |
| Viewing transactions | No |

## Lost Your 2FA Device?

1. Use a backup code to log in
2. Go to Settings > Security
3. Reset 2FA and set up again
4. If no backup codes, contact support

## Best Practices

Use an authenticator app over SMS
Back up your authenticator seed
Store backup codes securely
Update 2FA if changing phones
Don't screenshot QR codes
Don't share codes with anyone
    `.trim(),
  },
  {
    id: "gs-007",
    slug: "mobile-app-setup",
    title: "Mobile App Setup",
    description: "Access Supabank on your mobile device",
    category: "getting-started",
    order: 7,
    keywords: ["mobile", "app", "ios", "android", "phone"],
    content: `
# Mobile App Setup

Access Supabank on the go with our mobile-optimized web application.

## Accessing on Mobile

Supabank is a Progressive Web App (PWA), meaning you can add it to your home screen for an app-like experience.

### iOS (Safari)
1. Open Safari and go to supabank.xyz
2. Tap the Share button (square with arrow)
3. Scroll down and tap "Add to Home Screen"
4. Name it "Supabank" and tap Add
5. Launch from your home screen

### Android (Chrome)
1. Open Chrome and go to supabank.xyz
2. Tap the three-dot menu
3. Select "Add to Home Screen"
4. Confirm by tapping "Add"
5. Launch from your home screen

## Mobile Features

All desktop features are available:
- View balances and transactions
- Create liquidation addresses
- Track deposit status
- Manage bank accounts
- Contact support

## Mobile-Optimized Design

- Touch-friendly interface
- Optimized for smaller screens
- Bottom navigation for easy access
- Swipe gestures supported
- Dark mode support

## Notifications

To receive notifications:
1. Allow notifications when prompted
2. Enable in your device settings
3. Manage preferences in app settings

Types of notifications:
- Deposit confirmations
- Payout status updates
- Security alerts
- Important announcements

## Offline Access

Some features work offline:
- View cached transaction history
- Access help articles
- See account overview

Note: Transactions require internet connection.

## Tips for Mobile Use

Use a secure, private connection
Enable screen lock on your device
Log out when using shared devices
Keep your browser updated
Don't use public WiFi for transactions
    `.trim(),
  },
  {
    id: "gs-008",
    slug: "desktop-access",
    title: "Desktop Access",
    description: "Using Supabank on your computer",
    category: "getting-started",
    order: 8,
    keywords: ["desktop", "browser", "computer", "web", "access"],
    content: `
# Desktop Access

Access the full Supabank experience from your computer's web browser.

## Supported Browsers

| Browser | Minimum Version |
|---------|-----------------|
| Chrome | 90+ |
| Firefox | 88+ |
| Safari | 14+ |
| Edge | 90+ |

## Getting Started

1. Open your browser
2. Navigate to **supabank.xyz**
3. Click "Login" or "Get Started"
4. Enter your email for magic link
5. Check email and click to login

## Desktop Features

### Full Dashboard
- Complete transaction history
- Detailed analytics
- Advanced filtering options

### Keyboard Shortcuts
- \`Cmd/Ctrl + K\` - Quick search
- \`Cmd/Ctrl + N\` - New transaction
- \`Esc\` - Close dialogs

### Multiple Tabs
- Open multiple accounts
- Compare transactions
- Efficient workflow

## Optimal Display Settings

For best experience:
- Minimum screen width: 1280px
- Recommended: 1920x1080 or higher
- Zoom level: 100%

## Browser Settings

### Enable for Best Experience
JavaScript
Cookies (for session)
Local Storage
Web Notifications (optional)

### Privacy Mode
Supabank works in incognito/private mode, but:
- You'll need to login each time
- Preferences won't be saved
- 2FA may be required more often

## Bookmarking

Add Supabank to your bookmarks:
1. Press \`Cmd/Ctrl + D\`
2. Name it "Supabank"
3. Save to your bookmarks bar
4. Access with one click

## Troubleshooting

**Page not loading?**
- Clear browser cache
- Disable extensions temporarily
- Try a different browser

**Login issues?**
- Check email spam folder
- Ensure cookies are enabled
- Try incognito mode
    `.trim(),
  },
];
